// The SUPERSEDED-sample-id convention, in one place (Plannertech-user#4145).
//
// A sample number (`observationId`) is unique per team under
// (team, sampleName, analysisName, observationId). When a sample is failed and another row has to
// take over its number, the row that is LEAVING the number keeps it with a marker appended: two
// ZERO-WIDTH SPACES plus a uuid4. Two backend call sites create such a row, one per product —
// `fail_samples` supersedes its FAILED copy (non-LIMS), `lims/reruns` supersedes the failed
// ORIGINAL (LIMS, where that row carries the results and e-signatures justifying the failure).
//
// Mirrors `plannertech/sample_ids.py` in the backend. Keep the two in step.
//
// ── The rule ────────────────────────────────────────────────────────────────────────────────────
// The RAW value is the identity. It is what the backend stores, what every lookup matches on, and
// what the composite release key `productName|sampleName|observationId` is built from — a key the
// server persists as the release record's primary id and the FE matches back whole-string. So:
//
//   displaySampleId()  ONLY where a human reads the value.
//   NEVER on a React key, a group/map key, a `.find()`/`.filter()` predicate, a sort comparator,
//   a request payload, or anything compared for identity.
//
// Get that wrong and a release silently stops finding its sample group; the only symptom is a
// certificate rendered with an empty analysis list. That is why this file is the only place the
// marker is named.
//
// `restoreSampleIdSuffix` exists for the one field that is BOTH: the editable "Sample ID" input in
// AddEditSamplePopup shows the value to a user AND posts it back. It shows the clean number and
// re-attaches the stored suffix before submit, so editing a failed sample can never try to hand the
// number back and collide with the row that now owns it.

// Two ZERO WIDTH SPACE (U+200B) characters. Written as escapes on purpose: spelled literally it is
// an unreadable, un-greppable blank in the source.
export const SUPERSEDED_SAMPLE_ID_MARKER = "\u200b\u200b"

/** Has this sample number been handed over to another row? */
export function isSupersededSampleId(value) {
  if (value === null || value === undefined) return false
  return String(value).includes(SUPERSEDED_SAMPLE_ID_MARKER)
}

/**
 * The human-readable sample number: everything before the marker.
 *
 * RENDER-TIME ONLY — see the rule above. `null`/`undefined` pass through unchanged so callers can
 * keep their own `?? "—"` fallbacks.
 */
export function displaySampleId(value) {
  if (value === null || value === undefined) return value
  return String(value).split(SUPERSEDED_SAMPLE_ID_MARKER)[0]
}

/**
 * Re-attach `rawValue`'s stored suffix to a user-edited visible number.
 *
 * For an input that renders `displaySampleId(raw)` and posts the result back: the user edits the
 * part they can see, and the row keeps the suffix that makes it unique. A `rawValue` with no marker
 * returns `edited` untouched, so this is safe on every ordinary sample.
 */
export function restoreSampleIdSuffix(rawValue, edited) {
  if (!isSupersededSampleId(rawValue)) return edited
  const suffix = String(rawValue).slice(String(rawValue).indexOf(SUPERSEDED_SAMPLE_ID_MARKER))
  return `${edited ?? ""}${suffix}`
}
